import {
  Box,
  BoxProps,
  Button,
  Divider,
  Grid,
  Hidden,
  Typography
} from '@material-ui/core';
import React from 'react';

import { District, DistrictListing } from '../../../../services/APIService';
import ContactItem from './ContactItem';
import DistrictConnections from './DistrictConnections';
import DistrictFinancials from './DistrictFinancials';
import DistrictGeneral from './DistrictGeneral';

export interface DetailsPanelProps {
  district: DistrictListing & Partial<District>;
  onLocate: (bounds: [number, number, number, number]) => void;
  onClose: () => void;
}

const DetailsPanel: React.FC<DetailsPanelProps & BoxProps> = ({
  district,
  onLocate,
  onClose,
  ...rootProps
}) => {
  const hasContacts = !!(
    district.operators?.length ||
    district.attorneys?.length ||
    district.bookKeepers?.length ||
    district.assessors?.length
  );

  return (
    <Box {...rootProps} display="flex" flexDirection="column" overflow="hidden">
      <Box
        px={2}
        py={2}
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems="flex-start"
      >
        <Box mx={1} flex="1 1 auto">
          <Typography variant="h4">{district.name}</Typography>
        </Box>
        <Box
          flex="0 0 auto"
          mt={{ xs: 1, sm: 0 }}
          display="flex"
          flexDirection="row"
        >
          <Button
            variant="text"
            color="primary"
            style={{ minWidth: 0 }}
            onClick={onClose}
          >
            Back
          </Button>
        </Box>
      </Box>

      <Divider />

      <Box flex={1} px={1} overflow="hidden auto">
        {!!district.geoBounds && (
          <Hidden smDown>
            <Box px={2} pt={2} pb={1} display="flex">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() =>
                  onLocate(
                    district.geoBounds as Required<District['geoBounds']>
                  )
                }
              >
                Show on Map
              </Button>
            </Box>
          </Hidden>
        )}

        <Box px={2} pt={2} pb={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DistrictGeneral district={district} />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" gridGap={16}>
                <DistrictFinancials district={district} />
                <DistrictConnections district={district} />
              </Box>
            </Grid>
          </Grid>
        </Box>
        {hasContacts && (
          <React.Fragment>
            <Box px={2} py={1}>
              <Typography variant="h4" gutterBottom>
                Contacts
              </Typography>
            </Box>
            <Box px={2} pb={3}>
              <Grid container spacing={2}>
                {(district.operators || []).map((operator, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid item key={`OP${index}`} xs={6}>
                    <ContactItem
                      contact={operator}
                      type="Operator"
                      style={{ height: '100%' }}
                    />
                  </Grid>
                ))}
                {(district.attorneys || []).map((attorney, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid item key={`AT${index}`} xs={6}>
                    <ContactItem
                      contact={attorney}
                      type="Attorney"
                      style={{ height: '100%' }}
                    />
                  </Grid>
                ))}
                {(district.bookKeepers || []).map((bookKeeper, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid item key={`BK${index}`} xs={6}>
                    <ContactItem
                      contact={bookKeeper}
                      type="Book Keeper"
                      style={{ height: '100%' }}
                    />
                  </Grid>
                ))}
                {(district.assessors || []).map((assessor, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid item key={`AS${index}`} xs={6}>
                    <ContactItem
                      contact={assessor}
                      type="Assessor"
                      style={{ height: '100%' }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </React.Fragment>
        )}
      </Box>
    </Box>
  );
};

export default DetailsPanel;
